import { useEffect, useState } from "react";
import { Cpu, Sparkles, AlertTriangle, RotateCcw, Square, CheckCircle2, Loader2, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MODELS, LANGUAGES, modelConfig } from "@/lib/models";
import { LINE_PRESETS } from "@/lib/lines";
import { PhoneTest } from "./PhoneTest";
import { ERROR_HELP } from "@/lib/errors";
import { toast } from "sonner";

const ORDER = ["turbo", "small", "base", "tiny"];
const STAGES = [["extract", "Extract audio"], ["model", "Load model"], ["transcribe", "Transcribe speech"]];

const StageList = ({ stage }) => {
  const idx = STAGES.findIndex(([s]) => s === stage);
  return (
    <ol className="flex gap-3 text-xs flex-wrap">
      {STAGES.map(([s, l], i) => (
        <li key={s} className={`flex items-center gap-1 ${i === idx ? "text-[var(--cc-accent)]" : i < idx || stage === "done" ? "text-emerald-400" : "text-[var(--cc-muted)]"}`}>
          {i === idx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : i < idx || stage === "done" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
          {l}
        </li>
      ))}
    </ol>
  );
};

const DeviceCard = ({ device, onTest }) => (
  <div className="p-3 rounded-md bg-[var(--cc-panel)] border border-[var(--cc-border)] text-xs space-y-1" data-testid="device-check">
    <p className="font-semibold text-sm flex items-center gap-1.5"><Cpu className="h-4 w-4 text-[var(--cc-accent)]" /> Device check</p>
    {!device ? <p className="text-[var(--cc-text-2)]">Checking…</p> : (
      <>
        <p className="text-[var(--cc-text-2)]">WebGPU: <b className={device.webgpu ? "text-emerald-400" : "text-rose-300"}>{device.webgpu ? `available${device.f16 ? " (fp16)" : ""}` : "not available"}</b>
          {" · "}Memory: <b className="text-white">{device.memory ? `~${device.memory} GB` : "unknown"}</b>{" · "}CPU cores: <b className="text-white">{device.cores || "?"}</b></p>
        <p className="text-[var(--cc-text-2)]" data-testid="device-recommendation">{device.reason}</p>
        <button onClick={onTest} data-testid="open-phone-test-btn" className="mt-1 h-9 px-3 rounded-md border border-[var(--cc-border-strong)] font-semibold text-white hover:border-[var(--cc-accent)]">Run phone test (model + export)</button>
      </>
    )}
  </div>
);

export const GeneratePanel = ({ file, meta, device, tr, hasCaptions, onCaptions }) => {
  const [lang, setLang] = useState("auto");
  const [modelKey, setModelKey] = useState(null);
  const [forceCpu, setForceCpu] = useState(false);
  const [turboCpu, setTurboCpu] = useState(false);
  const [words, setWords] = useState(() => localStorage.getItem("cc_words") || "");
  const [consent, setConsent] = useState(false);
  const [wordTiming, setWordTiming] = useState(() => localStorage.getItem("cc_word_timing") !== "0");
  const [lines, setLines] = useState(() => localStorage.getItem("cc_lines") || "off");
  const [testOpen, setTestOpen] = useState(false);
  const { state } = tr;
  const busy = ["extract", "model", "transcribe"].includes(state.stage);

  useEffect(() => { if (device && !modelKey) setModelKey(device.recommended); }, [device, modelKey]);
  useEffect(() => { localStorage.setItem("cc_words", words); }, [words]);
  useEffect(() => { localStorage.setItem("cc_word_timing", wordTiming ? "1" : "0"); localStorage.setItem("cc_lines", lines); }, [wordTiming, lines]);

  const key = modelKey || "small";
  const cfg = modelConfig(key, device, forceCpu);
  const turboBlocked = key === "turbo" && cfg.device === "wasm" && !turboCpu;

  const start = async (c = cfg) => {
    setConsent(false);
    const language = LANGUAGES.find((l) => l.id === lang).code;
    const segs = await tr.run({ file, cfg: c, language, wordList: words, wordTiming, lines });
    if (segs) { toast.success(`${segs.length} captions generated on your device${wordTiming ? (segs.some((c) => c.words) ? " · exact word timing" : " · word timing estimated") : ""}`); onCaptions(segs); }
  };
  const generate = (c = cfg) => {
    if (!localStorage.getItem(`cc_model_${c.cacheKey}`)) return setConsent(true);
    start(c);
  };
  const smaller = ORDER[ORDER.indexOf(key) + 1];
  const err = state.error && (ERROR_HELP[state.error.code] || ERROR_HELP.UNKNOWN);

  return (
    <div className="space-y-4">
      <h2 className="text-xs font-semibold tracking-wider uppercase text-[var(--cc-text-2)]">2 · Generate captions</h2>
      <DeviceCard device={device} onTest={() => setTestOpen(true)} />
      <PhoneTest open={testOpen} onOpenChange={setTestOpen} device={device} />
      <div>
        <p className="text-sm font-semibold mb-1.5" id="lang-label">Spoken language</p>
        <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-labelledby="lang-label">
          {LANGUAGES.map((l) => (
            <button key={l.id} role="radio" aria-checked={lang === l.id} onClick={() => setLang(l.id)} disabled={busy} data-testid={`lang-${l.id}`}
              className={`h-11 rounded-md text-xs sm:text-sm font-semibold border ${lang === l.id ? "border-[var(--cc-accent)] bg-amber-500/10 text-[var(--cc-accent)]" : "border-[var(--cc-border-strong)] text-[var(--cc-text-2)]"}`}>{l.label}</button>
          ))}
        </div>
        {lang === "hinglish" && <p className="text-xs text-[var(--cc-text-2)] mt-1.5">Hinglish uses Whisper's Hindi mode: Hindi words usually come out in Devanagari, English words often in Latin letters — but Whisper may also write English words in Devanagari. Fix any word in the editor.</p>}
      </div>
      <div>
        <label htmlFor="model-select" className="text-sm font-semibold mb-1.5 block">AI model (runs on your device)</label>
        <select id="model-select" value={key} onChange={(e) => setModelKey(e.target.value)} disabled={busy} data-testid="model-select"
          className="w-full h-11 rounded-md bg-[var(--cc-elev)] border border-[var(--cc-border-strong)] px-3 text-sm">
          {ORDER.map((k) => <option key={k} value={k}>{`${MODELS[k].label} — ${modelConfig(k, device, forceCpu).sizeMB} MB · ${MODELS[k].note}${k === device?.recommended ? " (recommended)" : ""}`}</option>)}
        </select>
        <p className="text-xs text-[var(--cc-text-2)] mt-1.5" data-testid="model-mode">Runs on: <b className="text-white">{cfg.modeLabel}</b> · one-time download ~{cfg.sizeMB} MB, then cached in your browser.</p>
        <div className="mt-2 flex flex-col gap-1.5 text-xs text-[var(--cc-text-2)]">
          {device?.webgpu && <label className="flex items-center gap-2 min-h-[32px]"><input type="checkbox" checked={forceCpu} onChange={(e) => setForceCpu(e.target.checked)} data-testid="force-cpu-toggle" className="h-4 w-4" /> Force CPU mode (use if WebGPU errors)</label>}
          {key === "turbo" && cfg.device === "wasm" && !device?.mobile && <label className="flex items-center gap-2 min-h-[32px]"><input type="checkbox" checked={turboCpu} onChange={(e) => setTurboCpu(e.target.checked)} data-testid="turbo-cpu-toggle" className="h-4 w-4" /> Allow Turbo on CPU (desktop only, ~1.1 GB, very slow)</label>}
        </div>
        {turboBlocked && <p className="text-xs text-amber-300 mt-1" data-testid="turbo-blocked">Turbo needs WebGPU on this browser. Choose Whisper Small{device?.mobile ? "" : " or allow Turbo on CPU"}.</p>}
      </div>
      <div>
        <label htmlFor="word-list" className="text-sm font-semibold block">Custom words <span className="font-normal text-[var(--cc-text-2)]">(optional)</span></label>
        <p className="text-xs text-[var(--cc-text-2)] mb-1.5">One per line. Use <code className="text-amber-300">wrong =&gt; right</code> or just the preferred spelling, e.g. <code className="text-amber-300">insta gram =&gt; Instagram</code>.</p>
        <textarea id="word-list" value={words} onChange={(e) => setWords(e.target.value)} rows={3} data-testid="custom-words-input" disabled={busy}
          className="w-full rounded-md bg-[var(--cc-elev)] border border-[var(--cc-border-strong)] p-2.5 text-sm deva" placeholder={"CaptionCraft\nyou tube => YouTube"} />
      </div>
      <div className="space-y-2 p-3 rounded-md bg-[var(--cc-panel)] border border-[var(--cc-border)]">
        <label className="flex items-start gap-2 text-sm min-h-[32px]">
          <input type="checkbox" checked={wordTiming} onChange={(e) => setWordTiming(e.target.checked)} disabled={busy} data-testid="word-timing-toggle" className="h-4 w-4 mt-0.5" />
          <span><b>Exact word timing</b> <span className="text-xs text-[var(--cc-text-2)] block">Uses Whisper's word timestamps for karaoke / word highlight. Slightly slower; falls back to estimates if the device can't do it.</span></span>
        </label>
        <label htmlFor="lines-select" className="text-sm font-semibold block">Short Reels lines</label>
        <select id="lines-select" value={lines} onChange={(e) => setLines(e.target.value)} disabled={busy} data-testid="lines-select"
          className="w-full h-11 rounded-md bg-[var(--cc-elev)] border border-[var(--cc-border-strong)] px-3 text-sm">
          {Object.entries(LINE_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <p className="text-xs text-[var(--cc-text-2)]">Optional: auto-split long captions into short, punchy lines. You can also do this later from the Captions tab.</p>
      </div>
      {meta.duration > 180 && device?.mobile && <p className="text-xs text-amber-300 flex gap-1.5" data-testid="long-video-warning"><AlertTriangle className="h-4 w-4 shrink-0" /> Videos over 3 minutes may crash low-memory phone browsers with large models.</p>}
      {busy ? (
        <Button onClick={tr.cancel} variant="outline" className="w-full h-12 text-base font-bold" data-testid="stop-transcription-btn"><Square className="h-4 w-4 mr-2" /> Stop</Button>
      ) : (
        <Button onClick={() => generate()} disabled={!file || turboBlocked || !device} className="w-full h-12 text-base font-bold" data-testid="generate-captions-btn">
          <Sparkles className="h-5 w-5 mr-2" /> {hasCaptions ? "Regenerate Captions" : "Generate Captions"}
        </Button>
      )}
      {!file && <p className="text-xs text-[var(--cc-muted)] -mt-2">Upload a video first.</p>}
      {(busy || state.stage === "done") && (
        <div className="p-3 rounded-md bg-[var(--cc-panel)] border border-[var(--cc-border)] space-y-2" data-testid="transcription-progress" aria-live="polite">
          <StageList stage={state.stage} />
          <div className="h-2 rounded bg-[var(--cc-hi)] overflow-hidden"><div className="h-full bg-[var(--cc-accent)] transition-[width]" style={{ width: `${Math.round(state.pct * 100)}%` }} /></div>
          <p className="text-xs text-[var(--cc-text-2)]" data-testid="progress-detail">{state.detail} {busy && state.pct > 0 && `(${Math.round(state.pct * 100)}%)`}</p>
          {busy && <p className="text-[11px] text-[var(--cc-muted)]">Keep this tab open. Large models can take a few minutes on phones.</p>}
        </div>
      )}
      {state.stage === "error" && err && (
        <div role="alert" className="p-3 rounded-md bg-rose-500/10 border border-rose-500/40 space-y-2" data-testid="transcription-error">
          <p className="font-bold text-rose-200 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {err.title}</p>
          <p className="text-sm text-rose-100/80">{err.tip}</p>
          <p className="text-[11px] font-mono-ts text-rose-200/60 break-words" data-testid="transcription-error-detail">{state.error.message}</p>
          <div className="flex flex-wrap gap-2">
            {state.error.code !== "NO_SPEECH" && state.error.code !== "NO_AUDIO" && <Button size="sm" onClick={() => generate()} data-testid="retry-transcription-btn"><RotateCcw className="h-4 w-4 mr-1" /> Retry</Button>}
            {smaller && ["OOM", "WORKER", "GPU", "UNKNOWN"].includes(state.error.code) && (
              <Button size="sm" variant="outline" data-testid="use-smaller-model-btn" onClick={() => { setModelKey(smaller); generate(modelConfig(smaller, device, forceCpu || state.error.code === "GPU")); if (state.error.code === "GPU") setForceCpu(true); }}>
                Switch to {MODELS[smaller].label} &amp; retry
              </Button>
            )}
          </div>
        </div>
      )}
      <p className="text-xs text-[var(--cc-muted)] leading-relaxed" data-testid="accuracy-notice">
        Accuracy notice: transcription quality depends on the language, accent, background noise/music and model size. Free Whisper models are good but imperfect for Hindi and especially Hinglish — please review every caption. Flagged captions are highlighted in red.
      </p>
      <Dialog open={consent} onOpenChange={setConsent}>
        <DialogContent className="bg-[var(--cc-panel)] max-w-md" data-testid="model-consent-dialog">
          <DialogHeader>
            <DialogTitle>Download {cfg.label}?</DialogTitle>
            <DialogDescription className="text-[var(--cc-text-2)]">
              The open-source AI model (~{cfg.sizeMB} MB) will be downloaded once from Hugging Face (a public model host) and cached in your browser. It may use mobile data. Your video and audio are NOT uploaded — speech recognition runs entirely on this device.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConsent(false)} data-testid="model-consent-cancel">Cancel</Button>
            <Button onClick={() => start()} data-testid="model-consent-confirm">Download &amp; transcribe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
